import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CASE_KINDS,
  CASE_KIND_MAP,
  CASE_STATUSES,
  CASE_STATUS_MAP,
  DISCIPLINE_KINDS,
  DISCIPLINE_KIND_MAP,
  MAX_PULSE_QUESTIONS,
  SUGGESTED_PULSE,
  type CaseKind,
  type DisciplineKindKey,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface CaseRow {
  id: string;
  number: string;
  kind: CaseKind;
  kindLabel: string;
  kindKind: string;
  disciplinary: boolean;
  status: 'NEW' | 'INVESTIGATING' | 'DECIDED' | 'CLOSED' | 'WITHDRAWN';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  live: boolean;
  writable: boolean;
  subject: string;
  description: string;
  confidential: boolean;
  severity: number;
  openedOn: string | null;
  dueOn: string | null;
  decision: string | null;
  closeNote: string | null;
  overdue: boolean;
  raisedBy: { id: string; name: string } | null;
  about: { id: string; name: string } | null;
  assignedTo: { id: string; name: string } | null;
  events: number;
  actions: number;
}

export interface ActionRow {
  id: string;
  kind: DisciplineKindKey;
  kindLabel: string;
  kindKind: string;
  kindNote: string;
  severity: number;
  outsidePath: string | null;
  outsideLabel: string | null;
  reason: string;
  issuedOn: string | null;
  effectiveFrom: string | null;
  days: number | null;
  amount: number | null;
  currency: string;
  posted: boolean;
  acknowledgedAt: string | null;
  expiresOn: string | null;
  lapsed: boolean;
  notes: string | null;
  employee: { id: string; name: string; code: string | null };
  case: { id: string; number: string } | null;
}

export interface SurveyRow {
  id: string;
  title: string;
  isOpen: boolean;
  opensOn: string | null;
  closesOn: string | null;
  notes: string | null;
  questionCount: number;
  responses: number;
}

interface Summary {
  open: number;
  investigating: number;
  overdue: number;
  actions: number;
  openSurvey: { id: string; title: string; responses: number } | null;
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
}

type Tab = 'cases' | 'actions' | 'pulse';

const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'cases', label: 'الحالات' },
  { key: 'actions', label: 'الإجراءات التأديبية' },
  { key: 'pulse', label: 'قياس الرضا' },
] as const;

const SEVERITY_LABELS: Readonly<Record<number, string>> = {
  1: 'بالغة',
  2: 'متوسّطة',
  3: 'خفيفة',
};

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * علاقات الموظفين.
 *
 * ## حالةٌ تُفتح، ويُسمع فيها، وتُقرَّر، ثمّ تُغلق
 *
 * والسجلُّ يُكتب ولا يُحذف منه شيء: من يقرؤه بعد سنتين — في تظلُّمٍ أو نزاعٍ
 * قضائيّ — يحتاج أن يرى ما سُمع ومتى وممّن. وملفٌّ تُمحى منه واقعةٌ لا
 * يصلح سندًا لأحد: لا للشركة ولا للموظّف.
 *
 * ## وما لا يقع هنا
 *
 * لا تنتهي خدمةُ أحدٍ ولا يُخصم من أحدٍ من هذه الشاشة. الإجراءُ قرارٌ
 * يُسجَّل، والخصمُ لا يصير مالًا حتى يُكتب سطرًا في المرتّب بضغطةٍ صريحةٍ
 * تُقرأ في القسيمة. وإنهاءُ الخدمة يقع في ملفّ الموظّف ويُوقَّع عليه.
 */
export function Relations() {
  const [tab, setTab] = useState<Tab>('cases');

  const summary = useQuery({
    queryKey: ['relations', 'summary'],
    queryFn: () => api<Summary>('/hr/relations/summary'),
  });

  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="علاقات الموظفين"
        subtitle="شكوى تُرفع فتُسمع وتُقرَّر، وإجراءٌ يُسجَّل بشدّته ومدّته، ورضًا يُقاس بلا أسماء"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="حالاتٌ مفتوحة"
            value={num(s.open)}
            note={`منها ${num(s.investigating)} قيد التحقيق`}
          />
          <Stat
            label="تأخّر البتُّ فيها"
            value={num(s.overdue)}
            note={s.overdue > 0 ? 'مضى موعدُها ولم تُقرَّر' : 'لا متأخّرات'}
          />
          <Stat
            label="إجراءاتٌ صدرت"
            value={num(s.actions)}
            note="في كل الوقت"
          />
          <Stat
            label="استبيانُ رضًا مفتوح"
            value={s.openSurvey ? num(s.openSurvey.responses) : '—'}
            note={
              s.openSurvey
                ? `إجابةً على «${s.openSurvey.title}»`
                : 'لا استبيانَ مفتوح'
            }
          />
        </div>
      )}

      {s && s.overdue > 0 && (
        <Notice title="حالاتٌ مضى موعدُها" icon="alert" kind="warning">
          {plural(s.overdue, WORDS.record)} ما زالت تنتظر قرارًا بعد موعدها. ومن
          رفع شكوى ولم يُردّ عليه يتعلّم ألّا يرفع.
        </Notice>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام علاقات الموظفين">
        {TABS.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'cases' && <CasesSection />}
      {tab === 'actions' && <ActionsSection />}
      {tab === 'pulse' && <PulseSection />}
    </div>
  );
}

/* ==========================================================================
   الحالات
   ========================================================================== */

function CasesSection() {
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [writing, setWriting] = useState(false);

  const query = { status: status || undefined, kind: kind || undefined };

  const list = useQuery({
    queryKey: ['relations', 'cases', query],
    queryFn: () => api<CaseRow[]>(`/hr/relations/cases${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          الحالات
        </h2>
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">كل الأنواع</option>
          {CASE_KINDS.map((k) => (
            <option key={k} value={k}>
              {CASE_KIND_MAP[k].label}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {CASE_STATUSES.map((k) => (
            <option key={k} value={k}>
              {CASE_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={() => setWriting(true)}>
          <Icon name="plus" size={15} />
          حالة
        </button>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا حالات"
          text="الحالةُ ملفٌّ يُفتح ويُسمع فيه ويُقرَّر ثمّ يُغلق — ولا يُحذف. ومن يقرؤه بعد سنتين يحتاج أن يرى ما سُمع ومتى وممّن."
          action={
            <button
              className="btn btn-primary"
              onClick={() => setWriting(true)}
            >
              افتح أوّل حالة
            </button>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الرقم</th>
                <th>الموضوع</th>
                <th>النوع</th>
                <th>عمّن</th>
                <th>يتولّاها</th>
                <th>الشدّة</th>
                <th>الموعد</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td className="tiny">{c.number}</td>
                  <td>
                    {c.subject}
                    {c.confidential && (
                      <div className="tiny mute">
                        <Icon name="eye" size={11} /> سرّيّة
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={c.kindKind}>{c.kindLabel}</Badge>
                  </td>
                  <td className="tiny">{c.about?.name ?? '—'}</td>
                  <td className="tiny">{c.assignedTo?.name ?? '—'}</td>
                  <td className="tiny">
                    {SEVERITY_LABELS[c.severity] ?? c.severity}
                  </td>
                  <td className="tiny">
                    {c.dueOn ? date(c.dueOn) : '—'}
                    {c.overdue && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        مضى
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={c.statusKind}>{c.statusLabel}</Badge>
                  </td>
                  <td>
                    <Link
                      className="btn btn-sm"
                      to={`/hr/relations/cases/${c.id}`}
                    >
                      افتحها
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <CaseDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

function CaseDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<CaseKind>('COMPLAINT');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [raisedById, setRaisedById] = useState('');
  const [aboutId, setAboutId] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [severity, setSeverity] = useState('2');
  const [openedOn, setOpenedOn] = useState(today);
  const [dueOn, setDueOn] = useState('');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      api<CaseRow>('/hr/relations/cases', {
        method: 'POST',
        body: {
          kind,
          subject,
          description,
          raisedById: raisedById || null,
          aboutId: aboutId || null,
          assignedToId: assignedToId || null,
          confidential,
          severity: Number(severity),
          openedOn,
          dueOn: dueOn || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('فُتحت الحالة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const def = CASE_KIND_MAP[kind];
  const ready =
    subject.trim().length >= 5 &&
    description.trim().length >= 20 &&
    (!def.needsSubject || Boolean(aboutId));

  return (
    <Modal
      title="حالة جديدة"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !ready}
            onClick={() => save.mutate()}
          >
            افتحها
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="ec-kind">
            النوع
          </label>
          <select
            id="ec-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as CaseKind)}
          >
            {CASE_KINDS.map((k) => (
              <option key={k} value={k}>
                {CASE_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{def.description}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ec-severity">
            الشدّة
          </label>
          <select
            id="ec-severity"
            className="select"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="1">بالغة</option>
            <option value="2">متوسّطة</option>
            <option value="3">خفيفة</option>
          </select>
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="ec-subject">
            الموضوع
          </label>
          <input
            id="ec-subject"
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label req" htmlFor="ec-desc">
            ما وقع
          </label>
          <textarea
            id="ec-desc"
            className="input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="ما وقع بالضبط، ومتى، ومن حضر — لا وصفًا لشخص"
          />
          {/*
            الوصفُ مطلوبٌ بطول: حالةٌ مكتوبةٌ بسطرٍ لا يُبنى عليها تحقيقٌ ولا
            قرار، ومن يقرؤها بعد شهرين لا يعرف ما وقع.
          */}
          <span className="tiny mute">
            يُقرأ بعد شهرين ممّن لم يحضر — فاكتبه كما يُقرأ حينها.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ec-raiser">
            من رفعها
          </label>
          <select
            id="ec-raiser"
            className="select"
            value={raisedById}
            onChange={(e) => setRaisedById(e.target.value)}
          >
            <option value="">— بلا اسم —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            تركُه فارغًا بلاغٌ بلا اسم — ويبقى كذلك في السجلّ.
          </span>
        </div>

        <div className="field">
          <label
            className={def.needsSubject ? 'label req' : 'label'}
            htmlFor="ec-about"
          >
            عمّن هي
          </label>
          <select
            id="ec-about"
            className="select"
            value={aboutId}
            onChange={(e) => setAboutId(e.target.value)}
          >
            <option value="">— ليست عن شخص —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {def.needsSubject && (
            <span className="tiny mute">
              المخالفةُ تُنسب إلى موظّف — ومنها وحدَها يُبنى الإجراء.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="ec-owner">
            من يتولّاها
          </label>
          <select
            id="ec-owner"
            className="select"
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value)}
          >
            <option value="">— بلا متولٍّ —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="ec-opened">
            فُتحت في
          </label>
          <input
            id="ec-opened"
            className="input"
            type="date"
            value={openedOn}
            onChange={(e) => setOpenedOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ec-due">
            يُبتُّ فيها قبل
          </label>
          <input
            id="ec-due"
            className="input"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
          <span className="tiny mute">
            من رفع شكوى ولم يُردّ عليه يتعلّم ألّا يرفع.
          </span>
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={confidential}
              onChange={(e) => setConfidential(e.target.checked)}
            />
            سرّيّة
          </label>
          {/*
            السرّيّةُ وسمٌ يُقرأ ويقيّد العرض، لا تعميةٌ في القاعدة: من
            يملك قراءةَ هذه الشاشة يقرأ. ويُقال ذلك بدل أن يُفهَم غيرُه.
          */}
          <span className="tiny mute">
            وسمٌ يُنبّه من يعرضها في اجتماع — لا تعميةً في القاعدة: من يملك فتح
            هذه الشاشة يقرؤها.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الإجراءات
   ========================================================================== */

function ActionsSection() {
  const [kind, setKind] = useState('');
  const [writing, setWriting] = useState(false);
  const [posting, setPosting] = useState<ActionRow | null>(null);

  const query = { kind: kind || undefined };

  const list = useQuery({
    queryKey: ['relations', 'actions', query],
    queryFn: () => api<ActionRow[]>(`/hr/relations/actions${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          الإجراءات التأديبية
        </h2>
        <select
          className="select"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">كل الأنواع</option>
          {DISCIPLINE_KINDS.map((k) => (
            <option key={k} value={k}>
              {DISCIPLINE_KIND_MAP[k].label}
            </option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={() => setWriting(true)}>
          <Icon name="plus" size={15} />
          إجراء
        </button>
      </div>

      <Notice title="ما يقع هنا وما يقع في بابه" icon="info">
        الإجراءُ قرارٌ يُسجَّل بشدّته ومدّةِ سقوطه. والخصمُ لا يصير مالًا حتى
        يُكتب سطرًا في المرتّب بضغطةٍ صريحة، وإنهاءُ الخدمة يقع في ملفّ الموظّف
        ويُوقَّع عليه. وإنذارٌ بلا مدّةِ سقوطٍ حكمٌ مؤبَّد — فتُكتب له سنةٌ حين
        لا تُكتب.
      </Notice>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا إجراءات"
          text="الإجراءُ يُبنى على حالةِ مخالفةٍ — أو يُكتب مباشرةً حين لا تحتاج المخالفةُ تحقيقًا."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الموظّف</th>
                <th>الإجراء</th>
                <th>السبب</th>
                <th>صدر في</th>
                <th>يسقط في</th>
                <th>أُبلغ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} style={a.lapsed ? { opacity: 0.55 } : undefined}>
                  <td>
                    {a.employee.name}
                    {a.case && (
                      <div className="tiny mute">
                        <Link
                          className="link"
                          to={`/hr/relations/cases/${a.case.id}`}
                        >
                          {a.case.number}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={a.kindKind}>{a.kindLabel}</Badge>
                    {a.amount !== null && (
                      <div className="tiny">
                        {money(a.amount, a.currency)}
                        {a.posted ? ' · نُفِّذ' : ' · لم يُنفَّذ بعد'}
                      </div>
                    )}
                    {a.days !== null && (
                      <div className="tiny mute">{num(a.days)} يومًا</div>
                    )}
                  </td>
                  <td className="tiny">{a.reason}</td>
                  <td className="tiny">{date(a.issuedOn ?? '')}</td>
                  <td className="tiny">
                    {a.expiresOn ? date(a.expiresOn) : '—'}
                    {a.lapsed && <div className="tiny mute">سقط أثرُه</div>}
                  </td>
                  <td className="tiny">
                    {a.acknowledgedAt ? date(a.acknowledgedAt) : '—'}
                  </td>
                  <td>
                    {a.kind === 'DEDUCTION' && !a.posted && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setPosting(a)}
                      >
                        نفّذ الخصم
                      </button>
                    )}
                    {a.outsidePath && a.kind !== 'DEDUCTION' && (
                      <Link className="btn btn-sm" to={a.outsidePath}>
                        {a.outsideLabel}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <ActionDialog onClose={() => setWriting(false)} />}
      {posting && (
        <PostDialog action={posting} onClose={() => setPosting(null)} />
      )}
    </div>
  );
}

export function ActionDialog({
  caseId,
  employeeId: fixedEmployee,
  onClose,
}: {
  caseId?: string;
  employeeId?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [employeeId, setEmployeeId] = useState(fixedEmployee ?? '');
  const [kind, setKind] = useState<DisciplineKindKey>('WRITTEN');
  const [reason, setReason] = useState('');
  const [issuedOn, setIssuedOn] = useState(today);
  const [days, setDays] = useState('');
  const [amount, setAmount] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
    enabled: !fixedEmployee,
  });

  const def = DISCIPLINE_KIND_MAP[kind];

  const save = useMutation({
    mutationFn: () =>
      api('/hr/relations/actions', {
        method: 'POST',
        body: {
          employeeId,
          caseId: caseId ?? null,
          kind,
          reason,
          issuedOn,
          days: def.needsDays && days !== '' ? Number(days) : null,
          amount: def.needsAmount && amount !== '' ? Number(amount) : null,
          expiresOn: expiresOn || null,
          notes: notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('صدر الإجراء.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const ready =
    Boolean(employeeId) &&
    reason.trim().length >= 20 &&
    (!def.needsAmount || Number(amount) > 0) &&
    (!def.needsDays || Number(days) > 0);

  return (
    <Modal
      title="إجراء تأديبيّ"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            disabled={save.isPending || !ready}
            onClick={() => save.mutate()}
          >
            أصدِره
          </button>
        </>
      }
    >
      <div className="form-grid">
        {!fixedEmployee && (
          <div className="field span-2">
            <label className="label req" htmlFor="da-emp">
              الموظّف
            </label>
            <select
              id="da-emp"
              className="select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">— اختر —</option>
              {(staff.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field span-2">
          <label className="label" htmlFor="da-kind">
            الإجراء
          </label>
          <select
            id="da-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as DisciplineKindKey)}
          >
            {DISCIPLINE_KINDS.map((k) => (
              <option key={k} value={k}>
                {DISCIPLINE_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{def.description}</span>
        </div>

        {def.outsidePath && (
          <div className="field span-2">
            <Notice title="ويُنفَّذ في بابه" icon="alert" kind="warning">
              ما يُكتب هنا قرارٌ يُسجَّل. و{def.outsideLabel}:{' '}
              <Link className="link" to={def.outsidePath}>
                افتح البابَ حين تريد تنفيذه
              </Link>
              .
            </Notice>
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="da-reason">
            السبب
          </label>
          <textarea
            id="da-reason"
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ما وقع بالضبط، ومتى، وما أثرُه"
          />
          <span className="tiny mute">
            يُقرأ في نزاعٍ بعد سنة — فاكتبه كما يُقرأ حينها.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="da-issued">
            صدر في
          </label>
          <input
            id="da-issued"
            className="input"
            type="date"
            value={issuedOn}
            onChange={(e) => setIssuedOn(e.target.value)}
          />
        </div>

        {def.needsDays && (
          <div className="field">
            <label className="label req" htmlFor="da-days">
              أيّام الوقف
            </label>
            <input
              id="da-days"
              className="input num"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
        )}

        {def.needsAmount && (
          <div className="field">
            <label className="label req" htmlFor="da-amount">
              مبلغ الخصم
            </label>
            <input
              id="da-amount"
              className="input num"
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="da-expires">
            يسقط أثرُه في
          </label>
          <input
            id="da-expires"
            className="input"
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
          <span className="tiny mute">
            للإنذارات: تُكتب له سنةٌ إن تُرك فارغًا. وإنذارٌ يبقى إلى الأبد ليس
            إنذارًا.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="da-notes">
            ملاحظات
          </label>
          <textarea
            id="da-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

function PostDialog({
  action,
  onClose,
}: {
  action: ActionRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [period, setPeriod] = useState(
    `${new Date().toISOString().slice(0, 7)}-01`,
  );

  const post = useMutation({
    mutationFn: () =>
      api(`/hr/relations/actions/${action.id}/post`, {
        method: 'POST',
        body: { period },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('كُتب الخصمُ في المرتّب.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التنفيذ'),
  });

  return (
    <Modal
      title="تنفيذ الخصم في المرتّب"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            disabled={post.isPending}
            onClick={() => post.mutate()}
          >
            نفّذه
          </button>
        </>
      }
    >
      <Notice title="ما يقع الآن" icon="alert" kind="warning">
        يُكتب سطرُ جزاءٍ بمبلغ {money(action.amount ?? 0, action.currency)} في
        مرتّب {action.employee.name}، ويظهر في قسيمته. ولا يُكتب مرّتين — فإن
        ضغطتَ ثانيةً يُردّ.
      </Notice>

      <div className="field mt-3">
        <label className="label req" htmlFor="pd-period">
          شهر الاستحقاق
        </label>
        <input
          id="pd-period"
          className="input"
          type="date"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
        <span className="tiny mute">
          الشهرُ الذي يُحمَّل عليه — لا يومُ الإصدار.
        </span>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   قياس الرضا
   ========================================================================== */

function PulseSection() {
  const [writing, setWriting] = useState(false);

  const list = useQuery({
    queryKey: ['relations', 'surveys'],
    queryFn: () => api<SurveyRow[]>('/hr/relations/surveys'),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          استبيانات الرضا
        </h2>
        <button className="btn btn-primary" onClick={() => setWriting(true)}>
          <Icon name="plus" size={15} />
          استبيان
        </button>
      </div>

      <Notice title="لا أسماء" icon="info">
        الإجابةُ بلا صاحب: لا يُحفظ معها اسمٌ ولا معرّف. ولا يُعرض رقمٌ — لا
        للاستبيان ولا لقسمٍ فيه — تحت خمس إجابات: متوسّطُ قسمٍ فيه ثلاثةٌ اسمٌ
        لثلاثة. وبلا هذا يُجاب عليه مرّةً ثمّ لا يجيب أحدٌ بصدقٍ بعدها.
      </Notice>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا استبيانات"
          text="أسئلةٌ تُسأل مرّةً ثمّ تُعاد بعد أشهر — والخبرُ في الفارق بين المرّتين لا في الرقم وحده."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الاستبيان</th>
                <th>الأسئلة</th>
                <th>الإجابات</th>
                <th>يُفتح</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>{s.title}</td>
                  <td className="num">{num(s.questionCount)}</td>
                  <td className="num">{num(s.responses)}</td>
                  <td className="tiny">{date(s.opensOn ?? '')}</td>
                  <td>
                    <Badge kind={s.isOpen ? 'info' : 'neutral'}>
                      {s.isOpen ? 'مفتوح' : 'مغلق'}
                    </Badge>
                  </td>
                  <td>
                    <Link
                      className="btn btn-sm"
                      to={`/hr/relations/surveys/${s.id}`}
                    >
                      افتحه
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <SurveyDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

function SurveyDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState('');
  const [opensOn, setOpensOn] = useState(today);
  const [closesOn, setClosesOn] = useState('');
  const [notes, setNotes] = useState('');
  const [questions, setQuestions] = useState(
    SUGGESTED_PULSE.map((q) => ({ key: q.key, text: q.text })),
  );

  const save = useMutation({
    mutationFn: () =>
      api('/hr/relations/surveys', {
        method: 'POST',
        body: {
          title,
          opensOn,
          closesOn: closesOn || null,
          notes: notes || null,
          questions: questions.filter(
            (q) => q.key.trim() && q.text.trim().length >= 5,
          ),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['relations'] });
      toast('كُتب الاستبيان.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title="استبيان رضًا جديد"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || title.trim().length < 3}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ps-title">
            الاسم
          </label>
          <input
            id="ps-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="نبض الفريق — الربع الأول ٢٠٢٦"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ps-opens">
            يُفتح في
          </label>
          <input
            id="ps-opens"
            className="input"
            type="date"
            value={opensOn}
            onChange={(e) => setOpensOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ps-closes">
            يُغلق في
          </label>
          <input
            id="ps-closes"
            className="input"
            type="date"
            value={closesOn}
            onChange={(e) => setClosesOn(e.target.value)}
          />
          {/*
            التاريخُ يُقرأ ولا يغلق بنفسه: لا مهمّةَ مجدولةً في هذا النظام
            تغلق الاستبيانات، والإغلاقُ بزرٍّ أصدقُ من تاريخٍ لا يبلغه شيء.
          */}
          <span className="tiny mute">
            يُقرأ ولا يُغلق بنفسه — الإغلاقُ بزرٍّ في صفحة الاستبيان.
          </span>
        </div>

        <div className="field span-2">
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <span className="label" style={{ flex: 1 }}>
              الأسئلة — كلُّها بدرجةٍ من خمس
            </span>
            <button
              className="btn btn-sm"
              type="button"
              disabled={questions.length >= MAX_PULSE_QUESTIONS}
              onClick={() =>
                setQuestions([...questions, { key: '', text: '' }])
              }
            >
              <Icon name="plus" size={13} />
              سؤال
            </button>
          </div>

          <div className="table-wrap mt-2">
            <table className="table">
              <thead>
                <tr>
                  <th>المفتاح</th>
                  <th>السؤال</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {questions.map((q, i) => (
                  <tr key={i}>
                    <td style={{ width: 150 }}>
                      <input
                        className="input"
                        value={q.key}
                        onChange={(e) => {
                          const next = [...questions];
                          next[i] = { ...q, key: e.target.value.toUpperCase() };
                          setQuestions(next);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        value={q.text}
                        onChange={(e) => {
                          const next = [...questions];
                          next[i] = { ...q, text: e.target.value };
                          setQuestions(next);
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-ghost"
                        type="button"
                        aria-label="احذف السؤال"
                        onClick={() =>
                          setQuestions(questions.filter((_, j) => j !== i))
                        }
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <span className="tiny mute">
            الأسئلةُ لا تتغيّر بعد أوّل إجابة: الدرجاتُ مخزَّنةٌ بمفاتيحها،
            وسؤالٌ يتغيّر نصُّه يجعل درجاتٍ تُقرأ على سؤالٍ لم يُسأله أحد.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ps-notes">
            ما يُقال لمن يجيب
          </label>
          <textarea
            id="ps-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="إجابتك بلا اسم، ولا يُعرض رقمٌ عن مجموعةٍ أقلّ من خمسة"
          />
        </div>
      </div>
    </Modal>
  );
}
